import React, { useState } from "react";
import { Send, MessageSquare, CheckCircle, AlertCircle } from "lucide-react";
import { submitFeedback } from "../../core/features/feedback/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Card, 
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
import { Page } from "components/ui/page";
import PageHeader from "components/ui/PageHeader";

const FeedbackView = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await submitFeedback(formData);
      // If API call succeeds (200 status), show success
      setSubmitStatus("success");
      // Reset form
      setFormData({
        name: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    /* Was a one-off header: `min-h-screen` inside the shell's scroll container
       (forcing a viewport of dead space), a second coat of the canvas colour on
       top of the canvas, a hardcoded `bg-blue-500` tile that belongs to no tone,
       and a `text-3xl` heading found on no other page. */
    <Page width="narrow">
      <PageHeader
        icon={<MessageSquare className="w-5 h-5" />}
        tone="brand"
        title="Feedback"
        subtitle="We'd love to hear from you. Share your thoughts and suggestions."
      />

        {/* Feedback Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>Send us your feedback</CardTitle>
            <CardDescription>
              Your feedback helps us improve our platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                />
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              {/* Message Field */}
              <div className="space-y-2">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us what you think..."
                  required
                  rows={6}
                  className="min-h-[150px]"
                />
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-between">
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingPlacement="start"
                  disabled={isSubmitting}
                >
                  <Send className="w-4 h-4" />
                  Submit Feedback
                </Button>
              </div>

              {/* Success/Error Messages */}
              {submitStatus === "success" && (
                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <p className="text-green-800 dark:text-green-300">
                    Thank you for your feedback! We've received your message.
                  </p>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-red-800 dark:text-red-300">
                    Oops! Something went wrong. Please try again later.
                  </p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Info Section */}
        <Card className="mt-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-content dark:text-white mb-3">
              What happens next?
            </h3>
            <ul className="space-y-2 text-content-secondary dark:text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 dark:text-blue-400 mt-1">•</span>
                <span>Our team will review your feedback within 24-48 hours</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 dark:text-blue-400 mt-1">•</span>
                <span>If we need more information, we'll reach out via email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 dark:text-blue-400 mt-1">•</span>
                <span>Your feedback helps us improve the platform for everyone</span>
              </li>
            </ul>
          </CardContent>
        </Card>
    </Page>
  );
};

export default FeedbackView;
